<template>
    <div class="template-preview">
        <a-spin :spinning="loading">
            <div v-if="template" class="preview-body">
                <div v-if="template.description" class="description">{{ template.description }}</div>

                <section class="section">
                    <h4 class="section-title">
                        <i class="material-icons">table_chart</i>
                        {{
                            $t("tables.templates.tablesSectionTitle", {
                                selected: selectedCount,
                                total: template.tables.length,
                            })
                        }}
                        <i
                            class="material-icons info-icon"
                            v-tippy="{ content: $t('tables.templates.tablesSectionInfo') }"
                        >help_outline</i>
                    </h4>
                    <div class="tables-list">
                        <div
                            v-for="tbl in template.tables"
                            :key="tbl.key"
                            class="template-table-row"
                            @click="toggleTable(tbl.key)"
                        >
                            <a-checkbox
                                :checked="!!selectedTableKeys[tbl.key]"
                                v-tippy="{ content: $t('tables.templates.tableCheckboxTooltip') }"
                                @click.stop
                                @change="(e) => onTableCheckboxChange(tbl.key, e)"
                            />
                            <div class="table-info">
                                <div class="table-name">
                                    <i v-if="tbl.icon" class="material-icons">{{ tbl.icon }}</i>
                                    {{ tbl.name }}
                                </div>
                                <div v-if="tbl.description" class="table-desc">{{ tbl.description }}</div>
                                <div class="field-list" @click.stop>
                                    <span
                                        v-for="f in displayedFields(tbl)"
                                        :key="f.key"
                                        class="field-chip"
                                        :class="{ 'is-added': f.isAdded }"
                                        v-tippy="{ content: fieldChipTooltip(f) }"
                                    >
                                        {{ f.name }}
                                        <small>{{ fieldTypeLabel(f.fieldType) }}</small>
                                        <i
                                            class="material-icons chip-remove"
                                            v-tippy="{ content: $t('tables.templates.removeField') }"
                                            @click.stop="removeField(tbl.key, f)"
                                        >close</i>
                                    </span>

                                    <a-popover
                                        :visible="!!showTypePicker[tbl.key]"
                                        trigger="click"
                                        placement="bottom"
                                        overlayClassName="template-field-picker-popover"
                                        @visibleChange="(v) => onTypePickerVisibleChange(tbl.key, v)"
                                    >
                                        <span
                                            class="field-chip add-chip"
                                            v-tippy="{ content: $t('tables.templates.addFieldTooltip') }"
                                        >
                                            <i class="material-icons">add</i>
                                            {{ $t("tables.templates.addField") }}
                                        </span>
                                        <template slot="content">
                                            <FieldSelector
                                                @field-selected="(t) => onTypeSelected(tbl.key, t)"
                                                @close="closeTypePicker(tbl.key)"
                                            />
                                        </template>
                                    </a-popover>

                                    <span
                                        v-if="showAddForm[tbl.key]"
                                        class="field-add-form"
                                        @click.stop
                                    >
                                        <i class="material-icons add-type-icon">{{ pickerTypeIcon(newFieldForm[tbl.key] && newFieldForm[tbl.key].fieldType) }}</i>
                                        <small class="add-type-label">{{ fieldTypeLabel(newFieldForm[tbl.key] && newFieldForm[tbl.key].fieldType) }}</small>
                                        <input
                                            v-model="newFieldForm[tbl.key].name"
                                            ref="newFieldInput"
                                            type="text"
                                            class="add-name-input"
                                            :placeholder="$t('tables.templates.fieldNamePlaceholder')"
                                            @keyup.enter="saveNewField(tbl.key)"
                                            @keyup.esc="cancelAddField(tbl.key)"
                                        />
                                        <i
                                            class="material-icons add-confirm"
                                            :title="$t('tables.templates.save')"
                                            @click.stop="saveNewField(tbl.key)"
                                        >check</i>
                                        <i
                                            class="material-icons add-cancel"
                                            :title="$t('tables.templates.cancel')"
                                            @click.stop="cancelAddField(tbl.key)"
                                        >close</i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section v-if="template.relations && template.relations.length > 0" class="section">
                    <h4 class="section-title">
                        <i class="material-icons">hub</i>
                        {{ $t("tables.templates.relationsSectionTitle", { count: template.relations.length }) }}
                        <i
                            class="material-icons info-icon"
                            v-tippy="{ content: $t('tables.templates.relationsSectionInfo') }"
                        >help_outline</i>
                    </h4>
                    <div class="relations-list">
                        <div
                            v-for="(rel, idx) in template.relations"
                            :key="idx"
                            class="relation-row"
                            :class="{ disabled: !isRelationActive(rel) }"
                        >
                            <span class="rel-name">{{ rel.displayName }}</span>
                            <span
                                class="rel-side"
                                v-tippy="{ content: relationSideTooltip(rel.from) }"
                            >
                                {{ tableName(rel.from.tableKey) }}
                                <small v-if="rel.from.connectionType === 'field'">.{{ rel.from.fieldKey }}</small>
                            </span>
                            <i class="material-icons rel-arrow">arrow_forward</i>
                            <span
                                class="rel-side"
                                v-tippy="{ content: relationSideTooltip(rel.to) }"
                            >
                                {{ tableName(rel.to.tableKey) }}
                                <small v-if="rel.to.connectionType === 'field'">.{{ rel.to.fieldKey }}</small>
                            </span>
                            <span
                                class="rel-badge"
                                v-tippy="{ content: $t('tables.templates.relationBidirectional', { from: tableName(rel.from.tableKey), to: tableName(rel.to.tableKey) }) }"
                            >⇄</span>
                            <i
                                class="material-icons info-icon"
                                v-tippy="{ content: $t('tables.templates.relationExplanation', { from: tableName(rel.from.tableKey), to: tableName(rel.to.tableKey) }) }"
                            >help_outline</i>
                            <span
                                v-if="!isRelationActive(rel)"
                                class="rel-skip"
                                v-tippy="{ content: $t('tables.templates.skipReasonTooltip') }"
                            >
                                <i class="material-icons">block</i>
                                {{ $t("tables.templates.relationSkipped") }}
                            </span>
                        </div>
                    </div>
                </section>

                <section v-if="template.demoRecords" class="section demo-section">
                    <a-switch v-model="withDemoData" />
                    <div class="demo-info">
                        <div class="demo-title">{{ $t("tables.templates.demoToggleTitle") }}</div>
                        <div class="demo-subtitle">
                            {{ $t("tables.templates.demoToggleSubtitle") }}
                        </div>
                    </div>
                </section>

                <div class="actions">
                    <SupsisButton @click="$emit('cancel')" :disabled="applying">
                        {{ $t("tables.templates.cancel") }}
                    </SupsisButton>
                    <SupsisButton
                        primary
                        @click="apply"
                        :loading="applying"
                        :disabled="selectedCount === 0 || applying"
                    >
                        <i class="material-icons">check</i>
                        {{ $t("tables.templates.applyButton", { count: selectedCount }) }}
                    </SupsisButton>
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import { TABLE_TEMPLATE_API } from "../../../../api/tableTemplate";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import FieldSelector from "../detail/components/FieldSelector.vue";

// Field type → material icon mapping (FieldSelector ile aynı set)
const FIELD_TYPE_ICONS = {
    text: "text_fields", "long-text": "subject", number: "looks_one", currency: "attach_money",
    datetime: "event", dropdown: "arrow_drop_down", checkbox: "check_box",
    url: "link", email: "email", phone: "phone",
    contact: "person", task: "assignment", user: "support_agent", conversation: "message",
    record_id: "tag", record_created_at: "add_circle_outline", record_updated_at: "update",
    record_created_by: "person_add", record_updated_by: "person_outline",
    file: "attachment", image: "image", video: "videocam", audio: "audiotrack",
    "multi-file": "folder", "multi-image": "photo_library", "multi-video": "video_library", "multi-audio": "library_music",
    ai: "auto_awesome", formula: "functions", lookup: "sync_alt", "auto-increment": "plus_one",
    "automation-button": "smart_button", "linked-record": "switch_access_shortcut",
    json: "code", color: "palette",
};

export default {
    name: "TemplatePreview",
    components: { SupsisButton, FieldSelector },
    props: {
        templateKey: { type: String, required: true },
    },
    data() {
        return {
            loading: false,
            applying: false,
            template: null,
            selectedTableKeys: {},
            withDemoData: true,
            // Field customization state
            removedFieldKeys: {},   // { tableKey: [fieldKey, ...] }
            addedFields: {},        // { tableKey: [{ _localId, name, fieldType }] }
            showAddForm: {},        // { tableKey: bool }  — name input visible after type picked
            showTypePicker: {},     // { tableKey: bool }  — FieldSelector popover visible
            newFieldForm: {},       // { tableKey: { name, fieldType } }
        };
    },
    computed: {
        selectedCount() {
            return Object.values(this.selectedTableKeys).filter(Boolean).length;
        },
    },
    methods: {
        fieldTypeLabel(type) {
            const i18nKey = `tables.templates.fieldTypes.${type}`;
            const translated = this.$t(i18nKey);
            return translated === i18nKey ? type : translated;
        },
        tableName(key) {
            const t = (this.template && this.template.tables.find((x) => x.key === key)) || null;
            return t ? t.name : key;
        },
        isRelationActive(rel) {
            return !!this.selectedTableKeys[rel.from.tableKey] && !!this.selectedTableKeys[rel.to.tableKey];
        },
        toggleTable(key) {
            this.$set(this.selectedTableKeys, key, !this.selectedTableKeys[key]);
        },
        onTableCheckboxChange(key, e) {
            const checked = e && e.target ? e.target.checked : !this.selectedTableKeys[key];
            this.$set(this.selectedTableKeys, key, checked);
        },
        displayedFields(tbl) {
            const removed = this.removedFieldKeys[tbl.key] || [];
            const builtins = (tbl.fields || [])
                .filter((f) => !removed.includes(f.key))
                .map((f) => ({ key: f.key, name: f.name, fieldType: f.fieldType, isAdded: false }));
            const added = (this.addedFields[tbl.key] || []).map((f) => ({
                key: f._localId,
                name: f.name,
                fieldType: f.fieldType,
                isAdded: true,
            }));
            return [...builtins, ...added];
        },
        removeField(tableKey, field) {
            if (field.isAdded) {
                const next = (this.addedFields[tableKey] || []).filter((f) => f._localId !== field.key);
                this.$set(this.addedFields, tableKey, next);
            } else {
                const cur = this.removedFieldKeys[tableKey] || [];
                if (!cur.includes(field.key)) {
                    this.$set(this.removedFieldKeys, tableKey, [...cur, field.key]);
                }
            }
        },
        onTypePickerVisibleChange(tableKey, visible) {
            this.$set(this.showTypePicker, tableKey, visible);
        },
        closeTypePicker(tableKey) {
            this.$set(this.showTypePicker, tableKey, false);
        },
        onTypeSelected(tableKey, fieldType) {
            // FieldSelector tipini seçti — popover'ı kapat, name input formunu aç
            this.$set(this.newFieldForm, tableKey, { name: "", fieldType });
            this.$set(this.showAddForm, tableKey, true);
            this.$set(this.showTypePicker, tableKey, false);
            this.$nextTick(() => {
                // Focus name input
                const refs = this.$refs.newFieldInput;
                const el = Array.isArray(refs) ? refs[refs.length - 1] : refs;
                if (el && el.focus) el.focus();
            });
        },
        cancelAddField(tableKey) {
            this.$set(this.showAddForm, tableKey, false);
        },
        pickerTypeIcon(type) {
            return FIELD_TYPE_ICONS[type] || "label";
        },
        fieldChipTooltip(f) {
            const tooltipKey = f.isAdded
                ? "tables.templates.addedFieldChipTooltip"
                : "tables.templates.fieldChipTooltip";
            return this.$t(tooltipKey, { name: f.name, type: this.fieldTypeLabel(f.fieldType) });
        },
        relationSideTooltip(side) {
            const tableLbl = this.tableName(side.tableKey);
            if (side.connectionType === "_id") {
                // Bu tablonun ID'sine bağlanıyor
                return this.$t("tables.templates.fieldChipTooltip", {
                    name: tableLbl + " (_id)",
                    type: this.$t("tables.templates.fieldTypes.linked-record"),
                });
            }
            return this.$t("tables.templates.fieldChipTooltip", {
                name: tableLbl + "." + side.fieldKey,
                type: this.$t("tables.templates.fieldTypes.linked-record"),
            });
        },
        saveNewField(tableKey) {
            const form = this.newFieldForm[tableKey];
            if (!form || !form.name || !form.name.trim()) {
                this.$message.warning(this.$t("tables.templates.fieldNameRequired"));
                return;
            }
            const arr = (this.addedFields[tableKey] || []).slice();
            arr.push({
                _localId: `_added_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                name: form.name.trim(),
                fieldType: form.fieldType,
            });
            this.$set(this.addedFields, tableKey, arr);
            this.$set(this.showAddForm, tableKey, false);
        },
        buildFieldOverrides() {
            const result = {};
            for (const tableKey of Object.keys(this.selectedTableKeys)) {
                if (!this.selectedTableKeys[tableKey]) continue;
                const removed = this.removedFieldKeys[tableKey] || [];
                const added = (this.addedFields[tableKey] || []).map((f) => ({
                    name: f.name,
                    fieldType: f.fieldType,
                }));
                if (removed.length === 0 && added.length === 0) continue;
                result[tableKey] = { removed, added };
            }
            return result;
        },
        async fetchTemplate() {
            this.loading = true;
            try {
                const data = await TABLE_TEMPLATE_API.getByKey(this.templateKey);
                this.template = data;
                const sel = {};
                for (const t of data.tables || []) sel[t.key] = true;
                this.selectedTableKeys = sel;
            } catch (e) {
                console.error(this.$t("tables.templates.templateLoadFailed"), e);
                this.$message.error(this.$t("tables.templates.templateLoadFailed"));
            } finally {
                this.loading = false;
            }
        },
        apply() {
            const keys = Object.keys(this.selectedTableKeys).filter((k) => this.selectedTableKeys[k]);
            if (keys.length === 0) return;

            // Çoklu tablo limit kontrolü: seçili sayı + mevcut sayı > limit ise direkt upgrade modal
            const maxLimit = this.$feature.getFeature("maxCustomerTableCount");
            const currentCount = this.$store.state.table.totalTable || 0;
            const isAdmin = !!this.$store.state.auth.adminUser;
            const initializedFeatures = !!this.$store.state.site.initializedFeatures;
            const unlimited = maxLimit == null || maxLimit === -1;

            if (!isAdmin && initializedFeatures && !unlimited && currentCount + keys.length > maxLimit) {
                this.$feature.upgradeEnabled(
                    "customer-tables",
                    undefined,
                    "modal",
                    "maxCustomerTableCount"
                );
                return;
            }

            this.doApply(keys);
        },
        async doApply(keys) {
            this.applying = true;
            try {
                const result = await TABLE_TEMPLATE_API.apply(this.templateKey, {
                    selectedTableKeys: keys,
                    withDemoData: this.withDemoData,
                    fieldOverrides: this.buildFieldOverrides(),
                });
                this.$emit("applied", result);
            } catch (e) {
                console.error(this.$t("tables.templates.applyFailed"), e);
                const raw = (e.response && e.response.data) || e.message || "";
                const msg = String(raw);
                if (msg.indexOf("ErrorLimitation") !== -1 && msg.toLowerCase().indexOf("table") !== -1) {
                    this.$feature.upgradeEnabled(
                        "customer-tables",
                        undefined,
                        "modal",
                        "maxCustomerTableCount"
                    );
                } else if (msg.indexOf("ErrorLimitation") !== -1) {
                    this.$feature.upgradeEnabled("customer-tables", undefined, "modal");
                } else {
                    this.$message.error(msg || this.$t("tables.templates.applyFailed"));
                }
            } finally {
                this.applying = false;
            }
        },
    },
    created() {
        this.fetchTemplate();
    },
};
</script>

<style scoped lang="scss">
.template-preview {
    padding: 4px 4px;

    .preview-body {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .description {
        font-size: 12px;
        color: #555;
        line-height: 1.4;
        padding: 8px 12px;
        background: #fafafa;
        border-left: 3px solid #1890ff;
        border-radius: 4px;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .section-title {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 13px;
            font-weight: 600;
            margin: 0;
            color: #333;

            .material-icons {
                font-size: 16px;
                color: #1890ff;
            }

            .info-icon {
                font-size: 14px;
                color: #b0b0b0;
                cursor: help;
                margin-left: 2px;

                &:hover { color: #1890ff; }
            }
        }
    }

    .tables-list {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    // Modal'ın .custom-modal-body'si var(--color-bg-neutral-default) bg uyguluyor;
    // bu kart ve içindeki .table-info beyaz kalmalı (override için !important gerekli).
    .template-table-row {
        display: flex;
        gap: 8px;
        padding: 8px 10px;
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        cursor: pointer;
        transition: border-color 0.15s ease;
        background: #ffffff !important;
        align-items: flex-start;

        &:hover {
            border-color: #1890ff;
        }

        .table-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 3px;
            background: transparent !important;

            .table-name {
                font-size: 13px;
                font-weight: 600;
                color: #222;
                display: flex;
                align-items: center;
                gap: 5px;

                .material-icons {
                    font-size: 15px;
                    color: #666;
                }
            }

            .table-desc {
                font-size: 11px;
                color: #777;
            }

            .field-list {
                display: flex;
                gap: 3px;
                flex-wrap: wrap;
                margin-top: 2px;
                align-items: center;

                .field-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 2px 6px 2px 8px;
                    background: #f5f5f5;
                    border-radius: 10px;
                    font-size: 11px;
                    color: #555;
                    cursor: default;
                    transition: background-color 0.15s ease;

                    small {
                        color: #999;
                        font-size: 10px;
                    }

                    .chip-remove {
                        font-size: 12px;
                        color: #999;
                        cursor: pointer;
                        margin-left: 2px;
                        opacity: 0;
                        transition: opacity 0.15s ease, color 0.15s ease;

                        &:hover {
                            color: #d4380d;
                        }
                    }

                    &:hover .chip-remove {
                        opacity: 1;
                    }

                    &.is-added {
                        background: #e6f7ff;
                        color: #096dd9;

                        small {
                            color: #1890ff;
                        }
                    }

                    &.add-chip {
                        background: transparent;
                        border: 1px dashed #c0c0c0;
                        color: #1890ff;
                        cursor: pointer;
                        padding: 2px 8px;

                        .material-icons {
                            font-size: 12px;
                        }

                        &:hover {
                            border-color: #1890ff;
                            background: #f0f7ff;
                        }
                    }
                }

                .field-add-form {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 2px 4px 2px 6px;
                    background: #ffffff;
                    border: 1px solid #1890ff;
                    border-radius: 10px;
                    font-size: 11px;

                    .add-type-icon {
                        font-size: 14px;
                        color: #1890ff;
                    }

                    .add-type-label {
                        font-size: 10px;
                        color: #1890ff;
                        background: #e6f7ff;
                        border-radius: 6px;
                        padding: 1px 6px;
                    }

                    .add-name-input {
                        border: none;
                        outline: none;
                        font-size: 11px;
                        width: 120px;
                        background: transparent;
                        color: #222;
                    }

                    .add-confirm,
                    .add-cancel {
                        font-size: 14px;
                        cursor: pointer;
                        padding: 1px;
                        border-radius: 4px;
                        transition: background-color 0.15s ease, color 0.15s ease;
                    }

                    .add-confirm {
                        color: #389e0d;
                        &:hover { background: #f6ffed; }
                    }

                    .add-cancel {
                        color: #999;
                        &:hover { background: #fff1f0; color: #d4380d; }
                    }
                }
            }
        }
    }

    .relations-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .relation-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: #f5faff;
        border: 1px solid #d6e8ff;
        border-radius: 6px;

        &.disabled {
            background: #fafafa;
            border-color: #e8e8e8;
            opacity: 0.6;
        }

        .rel-name {
            font-size: 12px;
            font-weight: 600;
            color: #1890ff;
            margin-right: 4px;
        }

        .rel-side {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            font-size: 11px;
            color: #444;
            background: white;
            padding: 2px 7px;
            border-radius: 4px;
            border: 1px solid #e8e8e8;
            cursor: help;

            small {
                color: #999;
                font-size: 10px;
            }
        }

        .rel-arrow {
            font-size: 14px;
            color: #999;
        }

        .rel-badge {
            display: inline-flex;
            align-items: center;
            font-size: 11px;
            color: #389e0d;
            background: #f6ffed;
            border: 1px solid #b7eb8f;
            border-radius: 8px;
            padding: 0 6px;
            cursor: help;
            line-height: 1.5;
        }

        .info-icon {
            font-size: 14px;
            color: #b0b0b0;
            cursor: help;

            &:hover { color: #1890ff; }
        }

        .rel-skip {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            font-size: 10px;
            color: #d46b08;
            cursor: help;
            margin-left: auto;

            .material-icons {
                font-size: 12px;
            }
        }
    }

    .demo-section {
        flex-direction: row;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        background: #fffbe6;
        border: 1px solid #ffe58f;
        border-radius: 6px;

        .demo-info {
            display: flex;
            flex-direction: column;

            .demo-title {
                font-size: 12px;
                font-weight: 600;
                color: #614700;
            }

            .demo-subtitle {
                font-size: 11px;
                color: #8a6d00;
                line-height: 1.3;
            }
        }
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 6px;
        padding-top: 6px;
        border-top: 1px solid #f0f0f0;
    }
}
</style>

<style lang="scss">
// FieldSelector popover'ı geniş — ant-popover default max-width'i aşar
.template-field-picker-popover {
    .ant-popover-inner-content {
        padding: 12px;
        max-width: 90vw;
        max-height: 70vh;
        overflow: auto;
    }
}
</style>
